import { Lead } from "@justhome/common/api/core";
import { EMAIL_PATTERN, PHONE_PATTERN } from "@justhome/common/constants";
import { Button, InlineNotification, TextField } from "@justhome/react-ui";
import { useApplicationTranslation } from "@lib/hooks/translations";
import usePartnerContext from "@lib/hooks/usePartnerContext";
import clsx from "clsx";
import { useTranslation } from "next-i18next";
import { FC } from "react";
import { DeepPartial, useForm } from "react-hook-form";
import { OptInType } from "./types";

export type OptInFormData = Lead;

interface OptInFormProps {
  optInType: OptInType;
  onSubmit: (data: OptInFormData) => void;
  defaultValues?: DeepPartial<OptInFormData>;
  isLoading: boolean;
  isError: boolean;
  onPrevious?: VoidFunction;
  vertical?: boolean;
}

const OpInForm: FC<OptInFormProps> = ({
  onSubmit,
  optInType,
  defaultValues,
  isLoading,
  isError,
  onPrevious,
  vertical,
}) => {
  const { t } = useApplicationTranslation();
  const { partner } = usePartnerContext();
  const { t: tc } = useTranslation();
  const { register, formState, handleSubmit } = useForm<OptInFormData>({
    defaultValues,
    mode: "onBlur",
  });
  const { errors } = formState;

  let submit: any;

  switch (optInType) {
    case "certificate":
      submit = "opt_in_certificate_form_submit";
      break;
    case "before":
      submit = "form_button_next";
      break;
    default:
      submit = "opt_in_after_form_submit";
      break;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div
        className={clsx("flex flex-col m:grid gap-2s", {
          "grid-cols-12": !vertical,
          "grid-cols-6": vertical,
        })}
      >
        <div className="col-span-6">
          <TextField
            disabled={isLoading}
            {...register("firstName", {
              required: t("error_fill_in"),
            })}
            label={t("opt_in_form_first_name_label")}
            placeholder={t("opt_in_form_first_name_placeholder")}
            errorMessage={errors.firstName?.message}
          />
        </div>
        <div className="col-span-6">
          <TextField
            disabled={isLoading}
            {...register("lastName", {
              required: t("error_fill_in"),
            })}
            label={t("opt_in_form_last_name_label")}
            placeholder={t("opt_in_form_last_name_placeholder")}
            errorMessage={errors.lastName?.message}
          />
        </div>
        <div className="col-span-6">
          <TextField
            disabled={isLoading}
            {...register("email", {
              required: t("error_fill_in"),
              pattern: {
                value: EMAIL_PATTERN,
                message: tc("email_validation_error", { ns: "common" }),
              },
            })}
            label={t("opt_in_form_email_label")}
            placeholder={t("opt_in_form_email_placeholder")}
            errorMessage={errors.email?.message}
            inputMode="email"
          />
        </div>
        <div className="col-span-6">
          <TextField
            disabled={isLoading}
            {...register("phone", {
              required: t("error_fill_in"),
              pattern: {
                value: PHONE_PATTERN,
                message: t("error_phone_number"),
              },
            })}
            type="phone"
            label={t("opt_in_form_phone_number_label")}
            errorMessage={errors.phone?.message}
          />
        </div>
      </div>
      <div className="mt-3s">
        <div className="flex flex-col gap-2xs">
          <span
            className="text-app-body-2"
            dangerouslySetInnerHTML={{
              __html: t(
                !partner ? "form_consent_terms" : "form_consent_terms_partner",
              ),
            }}
          />
        </div>
      </div>
      {isError && (
        <InlineNotification className="mt-2s mb-s" type="error">
          {t("generic_error")}
        </InlineNotification>
      )}
      <div className="items-center flex justify-between s:justify-start mt-m">
        {onPrevious && (
          <Button
            disabled={isLoading}
            onClick={() => onPrevious()}
            className="mr-m"
            variant="ghost"
            paddingX={false}
            size="md"
          >
            {t("link_back_previous")}
          </Button>
        )}
        <Button
          type="submit"
          size={optInType === "before" ? "md" : "lg"}
          isLoading={isLoading}
        >
          {t(submit)}
        </Button>
      </div>
    </form>
  );
};

export default OpInForm;
